import { FC } from 'react'
import { useTranslation } from 'react-i18next'

import RefreshRounded from '@mui/icons-material/RefreshRounded'
import ReportProblemOutlined from '@mui/icons-material/ReportProblemOutlined'
import { Box, Button, Card, Typography, alpha, useTheme } from '@mui/material'

interface UAInsightsErrorStateProps {
  /** Overrides the generic body copy when a surface has something specific to say. */
  body?: string
  /** Omit to render the message without a retry action. */
  onRetry?: () => void
  /** Renders without the `Card` shell, for use inside a surface that already has one. */
  disableCard?: boolean
}

/**
 * Shown when a UA Insights request fails, in place of the surface's content.
 *
 * Distinct from the "not found" fallbacks: those mean the request succeeded
 * and there is genuinely nothing to show, so retrying would not help. This one
 * means the request itself failed, so it offers a retry.
 */
export const UAInsightsErrorState: FC<UAInsightsErrorStateProps> = ({ body, onRetry, disableCard }) => {
  const { t } = useTranslation('ua-insights')
  const theme = useTheme()

  const content = (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, textAlign: 'center', py: 2 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 44,
          height: 44,
          borderRadius: '50%',
          backgroundColor: alpha(theme.palette.warning.main, 0.12),
        }}
      >
        <ReportProblemOutlined sx={{ color: 'warning.main' }} />
      </Box>
      <Typography variant="h4">{t('load_error_title')}</Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 420 }}>
        {body ?? t('load_error_body')}
      </Typography>
      {onRetry && (
        <Button variant="outlined" size="small" startIcon={<RefreshRounded />} onClick={onRetry} sx={{ mt: 0.5 }}>
          {t('load_error_retry')}
        </Button>
      )}
    </Box>
  )

  if (disableCard) return content

  return (
    <Card variant="outlined" sx={{ p: 4 }}>
      {content}
    </Card>
  )
}
